import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api } from '../api/client';
import { SessionUser, TelegramAuthPayload } from '../api/types';

type SessionStatus = 'initializing' | 'authenticated' | 'unauthenticated';

interface SessionState {
  status: SessionStatus;
  user: SessionUser | null;
  login: (payload: TelegramAuthPayload) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionState | undefined>(undefined);

export function SessionProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<SessionStatus>('initializing');
  const [user, setUser] = useState<SessionUser | null>(null);

  const applySession = useCallback((payload: SessionUser | null) => {
    if (payload) {
      setUser(payload);
      setStatus('authenticated');
    } else {
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const session = await api.getSession();
      applySession(session.isAuthenticated ? session.user ?? null : null);
    } catch (error) {
      console.error('Failed to refresh session', error);
      applySession(null);
    }
  }, [applySession]);

  const login = useCallback(
    async (payload: TelegramAuthPayload) => {
      const response = await api.loginWithTelegram(payload);
      if (!response.isAuthenticated || !response.user) {
        throw new Error('Authentication failed');
      }
      applySession(response.user);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      applySession(null);
    }
  }, [applySession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      status,
      user,
      login,
      logout,
      refresh,
    }),
    [login, logout, refresh, status, user],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  return context;
}
